import { useTranslations } from '@vocab/react';
import { Drawer, Stack, Text } from 'braid-design-system';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ColumnCard } from 'src/components/Card/ColumnCard';
import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { PROFILE_INSIGHTS } from 'src/components/Router';
import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';

import translations from './.vocab';
import { ActivitiesSection } from './ActivitiesSection';
import { ViewsSection } from './ViewsSection';
import { profileInsightsLoaded } from './actionCreators';
import { useGetProfileInsights } from './data';

export const ProfileInsights = () => {
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { t } = useTranslations(translations);

  const { showProfileInsights, profileInsights, loading, error } =
    useGetProfileInsights();
  const { profileInsightsAnalyticsTracker, cardOpened } = useAnalytics();
  const showLoading = Boolean(loading || error);

  const toggleDrawer = () => {
    setShowDrawer((current) => !current);
    if (showProfileInsights) {
      profileInsightsAnalyticsTracker({
        eventName: 'profile_insights_widget_pressed',
      });
    }
  };

  useEffect(() => {
    if (!showLoading && showProfileInsights) {
      dispatch(profileInsightsLoaded());
    }
  }, [
    showLoading,
    showProfileInsights,
    profileInsightsAnalyticsTracker,
    dispatch,
  ]);

  useEffect(() => {
    if (showDrawer) {
      cardOpened({
        currentPage: 'profile talent search insights view',
        currentPageSection: 'talent search insights',
        currentPageSectionState: 'view',
      });
    }
  }, [cardOpened, showDrawer]);

  if (!showProfileInsights || !profileInsights) {
    return null;
  }

  const { viewCount, activities } = profileInsights || {};

  const cardButtonProps = {
    onClick: toggleDrawer,
    label: t('See activity'),
  };

  const loader = (
    <Stack space="gutter">
      <LoadingItem type="heading" level="1" />
      <LoadingItem type="text" size="standard" width="small" />
      <LoadingItem type="button" />
    </Stack>
  );

  return (
    <>
      <ColumnCard
        name={PROFILE_INSIGHTS}
        title={t('Profile activity')}
        loader={loader}
        loading={showLoading}
        buttonProps={cardButtonProps}
      >
        <Stack space="medium">
          <Text>{t('n profile views', { viewCount })}</Text>
        </Stack>
      </ColumnCard>
      <Drawer
        id="profile-insights-drawer"
        data={{ automation: 'profile-insights-drawer' }}
        title={t('Profile activity')}
        width="small"
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
      >
        <Stack space="xlarge">
          <ViewsSection viewCount={viewCount} />
          {activities.length > 0 ? (
            <ActivitiesSection activities={activities} />
          ) : null}
        </Stack>
      </Drawer>
    </>
  );
};
